import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import Logo from '../components/Logo';
import { getDailyQuote } from '../lib/dailyQuotes';

const getGreeting = (hour) => {
  if (hour < 5) return 'Working late';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Good night';
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());
  const quote = useMemo(() => getDailyQuote(now), [now.toDateString()]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) { toast.error('Please enter your credentials'); return; }
    setLoading(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Incorrect username or password');
    } finally { setLoading(false); }
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.04)',
    borderColor: 'rgba(201,169,97,0.25)',
    color: '#F5F1E8',
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#0E0E0E' }} data-testid="login-page">
      {/* LEFT: Brand panel */}
      <div className="hidden lg:flex flex-col flex-1 relative overflow-hidden px-16 py-14"
        style={{
          background: 'radial-gradient(ellipse at 25% 20%, rgba(201,169,97,0.08), transparent 55%), #0E0E0E',
        }}
      >
        {/* subtle grain */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'><filter id=\'n\'><feTurbulence baseFrequency=\'0.9\'/></filter><rect width=\'200\' height=\'200\' filter=\'url(%23n)\'/></svg>")' }}
        />

        <div className="relative z-10 flex flex-col h-full">
          <Logo size={48} showText light />

          <div className="flex-1 flex flex-col justify-center max-w-xl py-8">
            <p
              className="text-[10px] font-semibold uppercase mb-6"
              style={{ color: 'rgba(201,169,97,0.7)', letterSpacing: '0.32em' }}
              data-testid="login-greeting"
            >
              {getGreeting(now.getHours())} · {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>

            <p
              className="text-[10px] font-semibold uppercase mb-4"
              style={{ color: 'rgba(201,169,97,0.5)', letterSpacing: '0.32em' }}
            >
              Today's Reminder
            </p>

            <blockquote
              className="font-heading italic leading-[1.15] mb-8"
              style={{ color: '#F5F1E8', letterSpacing: '-0.015em', fontSize: 'clamp(34px, 3.6vw, 52px)' }}
              data-testid="login-daily-quote"
            >
              <span style={{ color: '#C9A961', fontStyle: 'normal' }}>“</span>
              {quote.q}
              <span style={{ color: '#C9A961', fontStyle: 'normal' }}>”</span>
            </blockquote>

            <p
              className="text-xs uppercase"
              style={{ color: 'rgba(245,241,232,0.45)', letterSpacing: '0.22em' }}
              data-testid="login-quote-author"
            >
              — {quote.a}
            </p>
          </div>

          <div
            className="pt-6 mt-auto border-t flex items-center justify-between text-[10px] tracking-[0.22em] uppercase"
            style={{ borderColor: 'rgba(201,169,97,0.15)', color: 'rgba(245,241,232,0.35)' }}
          >
            <span>Catalina Dental · Internal Use</span>
            <span>Rotates Daily · © {now.getFullYear()}</span>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden lg:block w-px" style={{ background: 'rgba(201,169,97,0.15)' }} />

      {/* RIGHT: Auth panel */}
      <div className="flex-1 lg:max-w-[460px] flex flex-col justify-center px-8 py-12" style={{ background: '#121212' }}>
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden mb-10"><Logo size={42} showText light /></div>

          <h2 className="font-heading text-3xl font-light mb-2" style={{ color: '#F5F1E8', letterSpacing: '-0.01em' }}>
            Sign in
          </h2>
          <p className="text-sm mb-10" style={{ color: 'rgba(245,241,232,0.5)' }}>
            Catalina Dental team access.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label
                className="text-[10px] font-semibold uppercase tracking-[0.18em]"
                style={{ color: 'rgba(201,169,97,0.85)' }}
              >
                Email
              </Label>
              <Input
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="you@catalinadentistry.com"
                type="email"
                style={inputStyle}
                className="h-12"
                autoComplete="username"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label
                  className="text-[10px] font-semibold uppercase tracking-[0.18em]"
                  style={{ color: 'rgba(201,169,97,0.85)' }}
                >
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs transition-colors hover:opacity-80"
                  style={{ color: 'rgba(245,241,232,0.5)' }}
                >
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={inputStyle}
                  className="h-12 pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'rgba(245,241,232,0.4)' }}
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 gap-2 font-semibold tracking-wider uppercase text-xs"
              disabled={loading}
              data-testid="login-submit-button"
              style={{
                background: '#C9A961',
                color: '#0E0E0E',
                letterSpacing: '0.18em',
              }}
            >
              {loading
                ? <><Loader2 size={15} className="animate-spin" /> Signing in…</>
                : <>Sign in <ArrowRight size={14} /></>}
            </Button>
          </form>

          <p className="text-center mt-10 text-xs" style={{ color: 'rgba(245,241,232,0.35)' }}>
            Need an account? Ask your administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
